import api from '@/lib/httpClient';

export interface ClientPreferenceOption {
  id: string;
  name: string;
  value: string;
  parentId?: string;
  label?: string;
  shortcut?: string;
  subtypes?: ClientPreferenceOption[];
}

export interface ClientPreferenceOptions {
  allergies: ClientPreferenceOption[];
  foodIntolerances: ClientPreferenceOption[];
  dietaryPreferences: ClientPreferenceOption[];
  cuisineTypes: ClientPreferenceOption[];
  medicalConditions: ClientPreferenceOption[];
  medicalConditionSubtypes: Record<string, ClientPreferenceOption[]>;
}

export interface ClientPreferenceOptionsResponse {
  success?: boolean;
  message?: string;
  data?: ClientPreferenceOptions | null;
}

function asOption(raw: unknown): ClientPreferenceOption | null {
  if (!raw || typeof raw !== 'object') return null;
  const record = raw as Record<string, unknown>;
  const value = String(record.value ?? record.id ?? '').trim();
  const name = String(record.name ?? record.label ?? value).trim();
  const id = String(record.id ?? value).trim();
  const parentId = String(record.parentId ?? record.parentValue ?? '').trim();
  const shortcut = String(record.shortcut ?? '').trim();
  if (!value || !name) return null;
  const subtypes = asOptionList(record.subtypes);
  return {
    id: id || value,
    name,
    value,
    ...(parentId ? { parentId } : {}),
    ...(shortcut ? { shortcut } : {}),
    ...(subtypes.length ? { subtypes } : {}),
  };
}

function asOptionList(raw: unknown): ClientPreferenceOption[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const options: ClientPreferenceOption[] = [];
  for (const item of raw) {
    const option = asOption(item);
    if (!option) continue;
    const key = option.value.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    options.push(option);
  }
  return options;
}

function asSubtypeMap(raw: unknown): Record<string, ClientPreferenceOption[]> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const result: Record<string, ClientPreferenceOption[]> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    const conditionId = key.trim().toLowerCase();
    const options = asOptionList(value);
    if (!conditionId || options.length === 0) continue;
    result[conditionId] = options;
  }
  return result;
}

const normalizePreferenceOptions = (raw: unknown): ClientPreferenceOptions => {
  const root =
    raw && typeof raw === 'object' && 'data' in (raw as object)
      ? (raw as { data: unknown }).data
      : raw;
  const record =
    root && typeof root === 'object' ? (root as Record<string, unknown>) : {};

  const medicalConditions = asOptionList(
    record.medicalConditions ?? record.medical_conditions,
  );
  const medicalConditionSubtypes = asSubtypeMap(
    record.medicalConditionSubtypes ?? record.medical_condition_subtypes,
  );

  for (const condition of medicalConditions) {
    if (condition.subtypes?.length) continue;
    const fromMap = medicalConditionSubtypes[condition.id.toLowerCase()];
    if (fromMap?.length) condition.subtypes = fromMap;
  }

  if (Object.keys(medicalConditionSubtypes).length === 0) {
    for (const condition of medicalConditions) {
      if (!condition.subtypes?.length) continue;
      medicalConditionSubtypes[condition.id.toLowerCase()] = condition.subtypes;
    }
  }

  return {
    allergies: asOptionList(record.allergies),
    foodIntolerances: asOptionList(
      record.foodIntolerances ?? record.intolerances ?? record.food_intolerances,
    ),
    dietaryPreferences: asOptionList(
      record.dietaryPreferences ?? record.preferences ?? record.dietary_preferences,
    ),
    cuisineTypes: asOptionList(
      record.cuisineTypes ?? record.cuisines ?? record.cuisine_types,
    ),
    medicalConditions,
    medicalConditionSubtypes,
  };
};

/** GET /clients/preference-options — allergies, intolerances, preferences, cuisine types, medical conditions. */
export const getClientPreferenceOptions = async (): Promise<ClientPreferenceOptions> => {
  const response = await api.get<ClientPreferenceOptionsResponse | ClientPreferenceOptions>(
    '/clients/preference-options',
  );
  return normalizePreferenceOptions(response.data);
};
